import { ReactNode, useCallback, useMemo } from 'react';

import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { PageTabs } from '@ui/components/PageTabs';
import { PaginationWithScroll } from '@ui/components/PaginationWithScroll/PaginationWithScroll';

import { KnockoutResultsRound } from '@features/shared/api/typings';
import { BracketsMatch } from '@features/tournament/ui/components/BracketsMatch';
import { KnockoutRoundName } from '@features/tournament/ui/components/KnockoutRoundName';

import styles from './TournamentKnockoutResults.module.css';

export function TournamentKnockoutResults({
  rounds,
  selectedRoundId,
  onSelectRound,
  onGameClick,
  renderShieldQuickPeek,
}: {
  rounds: KnockoutResultsRound[];
  selectedRoundId?: string;
  onSelectRound: (n: string) => void;
  onGameClick?: (gameId: string) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
}) {
  const isMobile = useMediaQuery() === 'mobile';

  const [visibleRounds, desktopRounds] = useMemo(() => {
    const filtered = rounds.filter(
      (r) =>
        r.roundType === 'WinnerBranch' || r.roundType === 'Final' || r.roundType === 'ThirdPlace',
    );

    const desktop = filtered.map((r) => ({
      label: <KnockoutRoundName preset="short" roundType={r.roundType} roundName={r.roundName} />,
      value: r.roundId,
    }));

    return [filtered, desktop];
  }, [rounds]);

  const currentRoundId = selectedRoundId ?? visibleRounds[visibleRounds.length - 1]?.roundId;
  const currentRoundIndex = visibleRounds.findIndex((r) => r.roundId === currentRoundId);
  const currentRoundNumber = currentRoundIndex + 1;

  const renderPaginationRound = useCallback(
    (roundNumber: number) => {
      const r = visibleRounds?.[roundNumber - 1];

      return r ? (
        <KnockoutRoundName roundType="WinnerBranch" preset="short" roundName={r.roundName} />
      ) : (
        roundNumber
      );
    },
    [visibleRounds],
  );
  const onPage = useCallback(
    (num: number) => onSelectRound(visibleRounds[num - 1]!.roundId),
    [onSelectRound, visibleRounds],
  );

  const currentRound =
    rounds.find((r) => r.roundId === currentRoundId) || rounds[rounds.length - 1];
  return (
    <div className={styles.rounds}>
      <div className={styles.title}>
        {isMobile ? (
          <PaginationWithScroll
            className={styles.paging}
            variant="new"
            pageNum={currentRoundNumber}
            pageTotal={visibleRounds.length}
            pageRender={renderPaginationRound}
            onPage={onPage}
          />
        ) : (
          <PageTabs
            tabClassName={styles.title}
            tabs={desktopRounds}
            value={currentRoundId!}
            onChange={onSelectRound}
            inverted
          />
        )}
      </div>

      <HorizontalScroll>
        <RoundResult
          key={currentRound?.roundId}
          round={currentRound}
          onGameClick={onGameClick}
          renderShieldQuickPeek={renderShieldQuickPeek}
        />
      </HorizontalScroll>
    </div>
  );
}

type RoundResultProps = {
  round?: KnockoutResultsRound;
  onGameClick?: (gameId: string) => void;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};

function RoundResult({ round, onGameClick, renderShieldQuickPeek }: RoundResultProps) {
  if (!round) return null;

  return (
    <div className={styles.round}>
      {round.matches.map((m) => (
        <BracketsMatch
          className={styles.match}
          key={'match' + m.player1?.userId + m.player2?.userId}
          roundName={m.knockoutRoundType === 'ThirdPlace' ? '3-е место' : undefined}
          player1={m.player1}
          player2={m.player2}
          isFinal={m.knockoutRoundType === 'Final'}
          isThirdPlace={m.knockoutRoundType === 'ThirdPlace'}
          onGameClick={onGameClick}
          renderShieldQuickPeek={renderShieldQuickPeek}
        />
      ))}
    </div>
  );
}
